import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Scale, Save, RotateCcw, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RATE_FIELDS = [
  { key: "cnss_rate", label: "CNSS employeur (%)", ref: "cnss_reference" },
  { key: "vf_rate", label: "Versement Forfaitaire (%)", ref: "vf_reference" },
  { key: "ta_rate", label: "Taxe d'apprentissage (%)", ref: "ta_reference" },
  { key: "cfpca_rate", label: "CFPCA / ONFPP (%)", ref: "cfpca_reference" },
  { key: "nationalization_min", label: "Quota de nationalisation (%)", ref: "nationalization_reference" },
];

const OTHER_FIELDS = [
  { key: "smig", label: "SMIG mensuel (GNF)" },
  { key: "feminization_target", label: "Objectif de féminisation (%)" },
  { key: "tolerance", label: "Tolérance d'écart (points)" },
  { key: "retirement_age", label: "Âge légal de retraite" },
  { key: "cdi_min", label: "Repère part des CDI (%)" },
  { key: "rotation_max", label: "Repère taux de rotation max (%)" },
  { key: "absenteeism_max", label: "Repère absentéisme max (%)" },
  { key: "cadres_min", label: "Repère taux de cadres (%)" },
  { key: "encadrement_min", label: "Repère taux d'encadrement (%)" },
];

const ALL_FIELDS = RATE_FIELDS.concat(OTHER_FIELDS);

const NumberField = ({ field, value, onChange }) => (
  <div className="space-y-1.5">
    <Label htmlFor={field}>{ALL_FIELDS.find((f) => f.key === field).label}</Label>
    <Input
      id={field}
      type="number"
      step="any"
      min={0}
      value={value ?? ""}
      onChange={(e) => onChange(field, e.target.value === "" ? "" : Number(e.target.value))}
      data-testid={`param-${field}-input`}
    />
  </div>
);

export const RegulatoryParametersCard = () => {
  const [values, setValues] = useState(null);
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API_URL}/api/parameters`);
    setValues(data.values);
    setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
  };

  useEffect(() => {
    load().catch(() => toast.error("Erreur lors du chargement des paramètres réglementaires"));
  }, []);

  const update = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== ""));
      const { data } = await axios.put(`${API_URL}/api/parameters`, payload);
      setValues(data.values);
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      toast.success("Paramètres réglementaires enregistrés");
    } catch (error) {
      const issue = error.response?.data?.detail?.[0];
      const field = ALL_FIELDS.find((f) => f.key === issue?.loc?.[issue.loc.length - 1]);
      toast.error(field ? `Valeur invalide pour « ${field.label} »` : "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Rétablir les valeurs réglementaires par défaut ?")) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/parameters/reset`);
      setValues(data.values);
      setMeta({});
      toast.success("Valeurs par défaut rétablies");
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  return (
    <Card data-testid="regulatory-parameters-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-primary" />
          Paramètres réglementaires
        </CardTitle>
        <CardDescription>
          Taux et seuils utilisés pour le contrôle de conformité du Rapport d'analyse du bilan social.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!values ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-3">
              {RATE_FIELDS.map((f) => (
                <div key={f.key} className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3 items-end">
                  <NumberField field={f.key} value={values[f.key]} onChange={update} />
                  <div className="space-y-1.5">
                    <Label htmlFor={f.ref} className="text-muted-foreground">Référence légale</Label>
                    <Input
                      id={f.ref}
                      value={values[f.ref] ?? ""}
                      onChange={(e) => update(f.ref, e.target.value)}
                      data-testid={`param-${f.ref}-input`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {OTHER_FIELDS.map((f) => (
                <NumberField key={f.key} field={f.key} value={values[f.key]} onChange={update} />
              ))}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground" data-testid="parameters-last-update">
                {meta.updated_at
                  ? `Dernière modification le ${new Date(meta.updated_at).toLocaleString("fr-FR")} par ${meta.updated_by}`
                  : "Valeurs par défaut en vigueur"}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={reset} data-testid="parameters-reset-btn">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Par défaut
                </Button>
                <Button onClick={save} disabled={saving} data-testid="parameters-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
